import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useApi } from '../hooks/useApi';
import type { Item, ItemPayload } from '../services/itemsService';
import AddIcon from '@mui/icons-material/Add';
import ErrorIcon from '@mui/icons-material/Error';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import SkipNextIcon from '@mui/icons-material/SkipNext';

const Items: React.FC = () => {
  const api = useApi();
  const [items, setItems] = useState<Item[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Partial<Item> | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input (300ms)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to page 1 when search changes
  useEffect(() => { setPage(1); }, [debouncedSearch]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.items.list(page, debouncedSearch);
      setItems(res.items);
      setTotal(res.total);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [api, page, debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  const showToast = (msg: string, type: 'success' | 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const openCreate = () => { setEditing({ prod_title: '', category: '', brand: '', sku: '' }); setShowModal(true); };
  const openEdit = (i: Item) => { setEditing({ ...i }); setShowModal(true); };

  const check = async () => {
    if (!editing) return;
    try {
      const payload: ItemPayload = {
        prod_title: editing.prod_title || '',
        category: editing.category || '',
        brand: editing.brand || '',
        sku: editing.sku || '',
      };
      if (editing.product_id) {
        await api.items.update(editing.product_id, payload);
        showToast('Item updated', 'success');
      } else {
        await api.items.create(payload);
        showToast('Item created', 'success');
      }
      setShowModal(false);
      setEditing(null);
      await load();
    } catch (err) {
      showToast((err as Error).message, 'error');
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await api.items.remove(id);
      showToast('Item deleted', 'success');
      await load();
    } catch (err) {
      showToast((err as Error).message, 'error');
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / 20));

  return (
    <div className="space-y-4 px-4 mt-4">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h1 className="font-headline-md sm:font-headline-lg font-bold text-on-surface">Inventory</h1>
          <p className="text-on-surface-variant font-body-xs sm:text-body-sm mt-0.5">
            {loading ? 'Loading...' : `${total} items total`}
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-1.5 sm:gap-2 bg-primary text-on-primary px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg font-body-xs sm:text-body-sm font-semibold hover:bg-primary/90 transition-colors shadow-md"
        >
          <AddIcon className="text-[16px] sm:text-[18px]" />
          Add Item
        </button>
      </div>

      {/* Card */}
      <div className="bg-surface border border-border-subtle rounded-xl shadow-sm overflow-hidden min-w-0">
        {/* Error banner */}
        {error && (
          <div className="flex items-center gap-2 bg-danger/10 border-b border-danger/20 text-danger text-body-sm px-4 py-3">
            <ErrorIcon className="text-[16px]" />
            {error}
          </div>
        )}

        {/* Search / filter bar */}
        <div className="px-4 py-3 border-b border-border-subtle">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant text-[18px] pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by ID, Title, Category, Brand, SKU..."
              className="w-full bg-surface-high border border-border-subtle rounded-lg pl-10 pr-3 py-2 text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
            />
          </div>
        </div>

        {/* Loading state */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <span className="text-on-surface-variant text-body-sm">Loading items...</span>
            </div>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border-subtle bg-surface-low">
                    <th className="text-left text-[10px] font-label-caps text-on-surface-variant uppercase tracking-wider px-3 py-2.5 hidden sm:table-cell">ID</th>
                    <th className="text-left text-[10px] font-label-caps text-on-surface-variant uppercase tracking-wider px-3 py-2.5">Title</th>
                    <th className="text-left text-[10px] font-label-caps text-on-surface-variant uppercase tracking-wider px-3 py-2.5 hidden md:table-cell">Category</th>
                    <th className="text-left text-[10px] font-label-caps text-on-surface-variant uppercase tracking-wider px-3 py-2.5 hidden lg:table-cell">Brand</th>
                    <th className="text-left text-[10px] font-label-caps text-on-surface-variant uppercase tracking-wider px-3 py-2.5 hidden md:table-cell">SKU</th>
                    <th className="text-right text-[10px] font-label-caps text-on-surface-variant uppercase tracking-wider px-3 py-2.5">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center">
                        <div className="flex flex-col items-center justify-center py-16">
                          <div className="w-12 h-12 rounded-full bg-surface-high flex items-center justify-center mb-3">
                            <Inventory2Icon className="text-on-surface-variant text-[24px]" />
                          </div>
                          <p className="text-on-surface-variant font-body-sm">
                            {debouncedSearch ? `No items match "${debouncedSearch}"` : 'No items found'}
                          </p>
                          <p className="text-on-surface-variant/60 text-xs mt-1">
                            {debouncedSearch ? 'Try a different search term' : 'Add items to see them here'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : items.map((i) => (
                    <tr key={i.product_id} className="border-b border-border-subtle last:border-0 hover:bg-surface-low/50 transition-colors">
                      <td className="text-on-surface font-mono text-body-xs sm:text-body-sm px-3 py-2.5 hidden sm:table-cell">{i.product_id}</td>
                      <td className="text-on-surface font-body-sm px-3 py-2.5">{i.prod_title}</td>
                      <td className="text-on-surface-variant font-body-sm px-3 py-2.5 hidden md:table-cell">{i.category}</td>
                      <td className="text-on-surface-variant font-body-sm px-3 py-2.5 hidden lg:table-cell">{i.brand}</td>
                      <td className="text-on-surface-variant font-mono text-body-xs sm:text-body-sm px-3 py-2.5 hidden md:table-cell">{i.sku}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center justify-end gap-1 sm:gap-2">
                          <button
                            onClick={() => openEdit(i)}
                            className="flex items-center gap-1 text-primary text-body-xs sm:text-body-sm hover:text-primary/80 transition-colors"
                          >
                            <EditIcon className="text-[14px] sm:text-[16px]" />
                            <span className="hidden sm:inline">Edit</span>
                          </button>
                          <button
                            onClick={() => remove(i.product_id)}
                            className="flex items-center gap-1 text-danger text-body-xs sm:text-body-sm hover:text-danger/80 transition-colors"
                          >
                            <DeleteIcon className="text-[14px] sm:text-[16px]" />
                            <span className="hidden sm:inline">Delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-3 sm:px-4 py-3 border-t border-border-subtle bg-surface-low">
              <span className="text-on-surface-variant text-body-xs sm:text-body-sm">
                Page {page}/{totalPages} ({total} total)
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(1)}
                  disabled={page <= 1}
                  className="w-8 h-8 rounded-lg text-body-xs border border-border-subtle text-on-surface-variant hover:bg-surface-high transition-colors disabled:opacity-40 disabled:cursor-not-allowed hidden sm:flex items-center justify-center"
                  title="First page"
                >
                  <SkipPreviousIcon className="text-[14px]" />
                </button>
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1}
                  className="flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-lg text-body-xs sm:text-body-sm border border-border-subtle text-on-surface-variant hover:bg-surface-high transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ArrowBackIcon className="text-[14px] sm:text-[16px]" />
                  <span className="hidden sm:inline">Prev</span>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                  .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                    if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, idx) =>
                    p === '...' ? (
                      <span key={`ellipsis-${idx}`} className="w-8 h-8 flex items-center justify-center text-body-xs text-on-surface-variant">...</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setPage(p as number)}
                        className={`w-8 h-8 rounded-lg text-body-xs border transition-colors ${p === page
                            ? 'bg-primary text-on-primary border-primary'
                            : 'border-border-subtle text-on-surface-variant hover:bg-surface-high'
                          }`}
                      >
                        {p}
                      </button>
                    )
                  )}
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages}
                  className="flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-lg text-body-xs sm:text-body-sm border border-border-subtle text-on-surface-variant hover:bg-surface-high transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <span className="hidden sm:inline">Next</span>
                  <ArrowForwardIcon className="text-[14px] sm:text-[16px]" />
                </button>
                <button
                  onClick={() => setPage(totalPages)}
                  disabled={page >= totalPages}
                  className="w-8 h-8 rounded-lg text-body-xs border border-border-subtle text-on-surface-variant hover:bg-surface-high transition-colors disabled:opacity-40 disabled:cursor-not-allowed hidden sm:flex items-center justify-center"
                  title="Last page"
                >
                  <SkipNextIcon className="text-[14px]" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modal — portaled to <body> to escape the space-y-4 wrapper,
          which would otherwise inject a 16px margin-top on the fixed layer. */}
      {showModal && editing && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowModal(false)} />
          <div className="relative bg-surface border border-border-subtle rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-toast-in">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border-subtle">
              <h2 className="font-headline-md text-on-surface font-semibold">
                {editing.product_id ? 'Edit Item' : 'Add Item'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-on-surface-variant hover:text-on-surface transition-colors">
                <CloseIcon className="text-[20px]" />
              </button>
            </div>

            <div className="p-4 sm:p-5 space-y-3 sm:space-y-4">
              <div>
                <label className="text-[10px] font-label-caps text-on-surface-variant uppercase block mb-1.5">Title</label>
                <input
                  type="text"
                  value={editing.prod_title || ''}
                  onChange={(e) => setEditing({ ...editing, prod_title: e.target.value })}
                  className="w-full bg-surface-high border border-border-subtle rounded-lg px-3 py-2 sm:py-2.5 text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                  placeholder="Product title"
                />
              </div>
              <div>
                <label className="text-[10px] font-label-caps text-on-surface-variant uppercase block mb-1.5">Category</label>
                <input
                  type="text"
                  value={editing.category || ''}
                  onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  className="w-full bg-surface-high border border-border-subtle rounded-lg px-3 py-2 sm:py-2.5 text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                  placeholder="Category"
                />
              </div>
              <div>
                <label className="text-[10px] font-label-caps text-on-surface-variant uppercase block mb-1.5">Brand</label>
                <input
                  type="text"
                  value={editing.brand || ''}
                  onChange={(e) => setEditing({ ...editing, brand: e.target.value })}
                  className="w-full bg-surface-high border border-border-subtle rounded-lg px-3 py-2 sm:py-2.5 text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                  placeholder="Brand"
                />
              </div>
              <div>
                <label className="text-[10px] font-label-caps text-on-surface-variant uppercase block mb-1.5">SKU</label>
                <input
                  type="text"
                  value={editing.sku || ''}
                  onChange={(e) => setEditing({ ...editing, sku: e.target.value })}
                  className="w-full bg-surface-high border border-border-subtle rounded-lg px-3 py-2 sm:py-2.5 text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                  placeholder="SKU"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 sm:gap-3 px-4 sm:px-5 py-3 sm:py-4 border-t border-border-subtle bg-surface-low">
              <button
                onClick={() => setShowModal(false)}
                className="px-3 sm:px-4 py-2 rounded-lg text-body-xs sm:text-body-sm text-on-surface-variant hover:bg-surface-high transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={check}
                className="flex items-center gap-1.5 sm:gap-2 bg-primary text-on-primary px-3 sm:px-4 py-2 rounded-lg font-body-xs sm:text-body-sm font-semibold hover:bg-primary/90 transition-colors shadow-md"
              >
                <CheckIcon className="text-[14px] sm:text-[16px]" />
                Save
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Toast — portaled to <body> to escape the space-y-4 wrapper, which would otherwise inject a 16px margin-top on the fixed layer. */}
      {toast && typeof document !== 'undefined' && createPortal(
        <div className={`fixed bottom-4 right-4 z-[100] flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-body-sm font-semibold animate-toast-in ${toast.type === 'success' ? 'bg-success text-white' : 'bg-danger text-white'
          }`}>
          {toast.type === 'success' ? <CheckIcon className="text-[16px]" /> : <ErrorIcon className="text-[16px]" />}
          {toast.msg}
        </div>,
        document.body
      )}
    </div>
  );
};

export default Items;
